import { useEffect, type RefObject } from 'react';

import type { KeyboardShortcutKeys } from '../../types/keyboard-shortcut-keys.js';
import { resolveKeyboardShortcutKeys } from '../../utils/keyboard-shortcut-utils.js';
import { matchesShortcut } from './matches-shortcut.js';
import { isValueEntryTarget } from './target-predicates.js';

/**
 * Accepted options for the `useKeyboardShortcut` hook.
 * @public
 */
export interface UseKeyboardShortcutOptions {
  /**
   * The combination that triggers the shortcut.
   */
  keys: KeyboardShortcutKeys;
  /**
   * Runs when the combination matches.
   */
  onTrigger: (event: KeyboardEvent) => void;
  /**
   * Where the listener is attached. A ref may still be `null` on the first
   * render; the listener attaches once it resolves.
   * @defaultValue document.body
   */
  eventTarget?: HTMLElement | RefObject<HTMLElement | null>;
  /**
   * Whether the shortcut currently listens.
   * @defaultValue true
   */
  enabled?: boolean;
  /**
   * Whether the browser's default action for the combination is suppressed on a
   * match.
   * @defaultValue true
   */
  preventDefault?: boolean;
  /**
   * Whether the listener runs in the capture phase, ahead of descendants that
   * call `stopPropagation`.
   * @defaultValue false
   */
  capture?: boolean;
  /**
   * Suppresses the shortcut when it returns `true` for the event's target.
   * @defaultValue isValueEntryTarget
   */
  shouldIgnoreTarget?: (target: EventTarget | null) => boolean;
}

function resolveEventTarget(
  eventTarget: UseKeyboardShortcutOptions['eventTarget'],
): HTMLElement | null {
  if (!eventTarget) {
    return document.body;
  }

  return eventTarget instanceof HTMLElement ? eventTarget : eventTarget.current;
}

/**
 * The `useKeyboardShortcut` hook registers a `keydown` listener for a key
 * combination in `aria-keyshortcuts` format and runs `onTrigger` when the user
 * presses it. On top of that, the hook accepts `mod`, which resolves to
 * `Meta` on macOS and to `Control` on every other platform.
 * @public
 */
export function useKeyboardShortcut({
  keys,
  onTrigger,
  eventTarget,
  enabled = true,
  preventDefault = true,
  capture = false,
  shouldIgnoreTarget = isValueEntryTarget,
}: UseKeyboardShortcutOptions): void {
  // No deps on purpose: re-resolves `eventTarget.current` after every render.
  useEffect(() => {
    const target = resolveEventTarget(eventTarget);

    if (!enabled || !target) {
      return;
    }

    const combination = resolveKeyboardShortcutKeys(keys);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreTarget(event.target)) {
        return;
      }

      if (!matchesShortcut(event, combination)) {
        return;
      }

      if (preventDefault) {
        event.preventDefault();
      }

      onTrigger(event);
    };

    target.addEventListener('keydown', handleKeyDown, capture);

    return () => {
      target.removeEventListener('keydown', handleKeyDown, capture);
    };
  });
}
